import { CurrencyWarningFeatureId } from "@opensea/graphql"
import { bn } from "@opensea/utils"
import { z } from "zod"
import { isAddress } from "../../../lib/address"
import { addressSchema } from "../../../lib/zod/address"

const currencySchema = z.object({
  contractAddress: z.string(),
  usdPrice: z.string(),
  restrictSwapsToNativeTokens: z.boolean().nullable(),
  restrictSwapsToSameChain: z.boolean().nullable(),
  symbol: z.string(),
  name: z.string(),
  imageUrl: z.string().nullable(),
  decimals: z.int(),
  isSwapDisabled: z.boolean(),
  warnings: z.array(z.object({ featureId: z.enum(CurrencyWarningFeatureId) })),
  chainIdentifier: z.string(),
  stats: z
    .object({
      marketCapUsd: z.number().nullable(),
      oneDay: z.object({
        priceChange: z.number().nullable(),
      }),
    })
    .nullable(),
})

export const swapFlowFormSchema = z
  .object({
    fromCurrencyBalance: z.float64().optional(),
    fromCurrency: currencySchema,
    fromQuantity: z.float64({
      error: () => "Please enter a valid quantity",
    }),
    toCurrency: currencySchema,
    toQuantity: z.float64(),
    transactionMode: z.enum(["buy", "sell"]).optional(),
    recipient: addressSchema,
    customDestinationWalletEnabled: z.boolean(),
    isUsdInputMode: z.boolean().default(false),
    maxSlippage: z.float64().superRefine((val, ctx) => {
      if (val > 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Slippage must be less than 100",
        })
        return
      }

      if (val < 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Slippage must be greater than 0",
        })
        return
      }

      const valStr = val.toString()
      if (valStr.includes(".") && valStr.split(".")[1]?.length > 2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Slippage must have less than 2 decimals",
        })
      }
    }),
  })
  .superRefine(({ fromQuantity, toQuantity, transactionMode }, ctx) => {
    // Conditional validation based on transaction mode
    const mode = transactionMode || "sell" // Default to sell mode if not specified

    if (mode === "sell") {
      // In sell mode, fromQuantity is the user input and must be > 0
      if (!fromQuantity || fromQuantity <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Quantity must be greater than 0",
          path: ["fromQuantity"],
        })
      }
    } else if (mode === "buy") {
      // In buy mode, toQuantity is the user input and must be > 0
      if (!toQuantity || toQuantity <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Quantity must be greater than 0",
          path: ["toQuantity"],
        })
      }
    }
  })
  .superRefine(({ fromCurrencyBalance, fromQuantity }, ctx) => {
    // Check if fromCurrencyBalance is a number (including 0)
    if (
      typeof fromCurrencyBalance === "number" &&
      fromQuantity > fromCurrencyBalance
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Insufficient balance",
        path: ["fromQuantity"],
      })
    }
  })
  .superRefine(
    (
      { fromCurrency, fromQuantity, toCurrency, toQuantity, transactionMode },
      ctx,
    ) => {
      const mode = transactionMode || "sell" // Default to sell mode if not specified

      if (mode === "sell") {
        // Validate fromQuantity decimals in sell mode
        const decimals = bn(fromQuantity).decimalPlaces()
        if (decimals !== null && decimals > fromCurrency.decimals) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Invalid decimal places",
            path: ["fromQuantity"],
          })
        }
      } else if (mode === "buy" && toCurrency) {
        // Validate toQuantity decimals in buy mode
        const decimals = bn(toQuantity).decimalPlaces()
        if (decimals !== null && decimals > toCurrency.decimals) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Invalid decimal places",
            path: ["toQuantity"],
          })
        }
      }
    },
  )
  .superRefine(({ recipient, customDestinationWalletEnabled }, ctx) => {
    if (
      customDestinationWalletEnabled &&
      !(recipient.resolvedAddress && isAddress(recipient.resolvedAddress))
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please enter a valid recipient address",
        path: ["recipient.resolvedAddress"],
      })
    }
  })
